import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFriendsList, getUsersList, EIM } from '@/infrastructure/sdk';
import { IS_CONTACT_SYNC_ENABLED, IS_MIRRORFLY_TEAMS_ENABLED } from '@/processENV';
import { debounce } from '@/shared/utils';
import { useIsOnline } from '@/app/store/navigationSlice';
import { setUserInfoMap } from '../../model';

const DEFAULT_PAGE = 1;

// ─── API Strategy ─────────────────────────────────────────────────────────────
const fetchByStrategy = async (search, page, size) => {
   if (IS_MIRRORFLY_TEAMS_ENABLED) {
      const res = await EIM.getEnterpriseUserList({ page, size, searchTerm: search });
      return {
         users: res?.users ?? [],
         hasMore: (res?.users ?? []).length === size,
         isServer: true,
      };
   }

   if (IS_CONTACT_SYNC_ENABLED) {
      const res = await getFriendsList();
      return {
         users: res?.users ?? [],
         hasMore: false, // one-shot, no pagination
         isServer: false,
      };
   }

   // Default — directory
   const res = await getUsersList(search, page, size);
   return {
      users: res?.users ?? [],
      hasMore: (res?.users ?? []).length === size,
      isServer: true,
   };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useUsersList = ({ size = 20, omitUserIds = [] } = {}) => {
   const [state, setState] = useState({
      users: [], // Stores [{ userId }]
      searchText: '',
      page: DEFAULT_PAGE,
      hasMore: true,
      loading: false,
      error: null,
   });

   const dispatch = useDispatch();
   const isOnline = useIsOnline();

   // ── Redux selectors ──────────────────────────────────────
   const userInfoMap = useSelector(s => s.userInfoData?.users);

   // ── Resolve and Filter Data ──────────────────────────────────────
   const data = useMemo(() => {
      const sourceList = state.users;
      const resolvedList = sourceList.map(item => userInfoMap?.[item.userId] || item);

      const query = state.searchText.toLowerCase().trim();
      const filtered = query
         ? resolvedList.filter(u => {
              const nickName = (u.nickName || '').toLowerCase();
              const name = (u.name || '').toLowerCase();
              const userId = (u.userId || '').toLowerCase();
              return nickName.includes(query) || name.includes(query) || userId.includes(query);
           })
         : resolvedList;

      const finalFiltered = omitUserIds.length > 0 ? filtered.filter(u => !omitUserIds.includes(u.userId)) : filtered;

      return finalFiltered.map(u => ({
         userId: u.userId,
         chatType: 'chat',
         isExternal: u.isExternal ?? false,
      }));
   }, [state.users, userInfoMap, state.searchText, omitUserIds]);

   // ── Refs ──────────────────────────────────────────────────────────────────
   const isFetching = useRef(false);
   // Last (search, page) we issued a request for. Multiple triggers race to load the
   // first page on mount — SmartListView auto-load, SearchBox's onChange-on-mount, and
   // (in StrictMode) the online-recovery effect — all asking for the same page. Keying
   // off this makes a given (search, page) idempotent so it only goes out once.
   // `force` (online recovery) bypasses it to pull fresh data after reconnecting.
   const requestedKeyRef = useRef(null);

   // ── Core fetch ────────────────────────────────────────────────────────────
   const fetchUsers = useCallback(
      async ({ search, page, reset = false, force = false }) => {
         const requestKey = `${search}::${page}`;
         if (isFetching.current) return;
         if (!force && requestedKeyRef.current === requestKey) return;
         requestedKeyRef.current = requestKey;
         isFetching.current = true;
         setState(prev => ({ ...prev, loading: true, error: null }));

         try {
            const { users: newUsers, hasMore } = await fetchByStrategy(search, page, size);

            // 1. Hydrate global profile map
            if (newUsers.length > 0) dispatch(setUserInfoMap(newUsers));

            // 2. Local updates
            const nextUsersIds = newUsers.map(u => ({ userId: u.userId }));
            const finalUsers = reset ? nextUsersIds : [...state.users, ...nextUsersIds];
            const nextPage = page + 1;

            setState(prev => ({
               ...prev,
               loading: false,
               users: finalUsers,
               searchText: search,
               page: nextPage,
               hasMore,
            }));
         } catch (error) {
            // Halt auto-load on failure (hasMore: false) so it doesn't keep
            // re-requesting the same page in a loop. A new search or online
            // recovery resets state and retries.
            setState(prev => ({
               ...prev,
               loading: false,
               hasMore: false,
               error: error?.message ?? 'Failed to load users',
            }));
         } finally {
            isFetching.current = false;
         }
      },
      [size, dispatch, state.users],
   );

   const fetchUsersRef = useRef(fetchUsers);
   fetchUsersRef.current = fetchUsers;

   const searchTextRef = useRef(state.searchText);
   searchTextRef.current = state.searchText;

   // ── Online Recovery ───────────────────────────────────────────────────────
   // Refetch only on a real offline→online transition. A "skip the first render"
   // ref guard is unreliable because StrictMode double-invokes the effect in dev —
   // the first run would consume the guard and the second would fire on mount.
   // Comparing the previous online value is transition-accurate and idempotent.
   const wasOnlineRef = useRef(isOnline);
   useEffect(() => {
      const cameOnline = isOnline && wasOnlineRef.current === false;
      wasOnlineRef.current = isOnline;
      if (cameOnline) {
         // Silently refetch page 1 for the current search; force past the dedupe
         // guard since the key is unchanged but we want fresh data.
         fetchUsersRef.current({
            search: searchTextRef.current,
            page: DEFAULT_PAGE,
            reset: true,
            force: true,
         });
      }
   }, [isOnline]);

   // ── Handlers ──────────────────────────────────────────────────────────────
   const handleSearch = useMemo(
      () =>
         debounce(text => {
            fetchUsersRef.current({ search: text, page: DEFAULT_PAGE, reset: true });
         }, 300),
      [],
   );

   const loadMore = useCallback(() => {
      if (!state.hasMore || state.loading || isFetching.current) return;
      fetchUsers({ search: state.searchText, page: state.page, reset: false });
   }, [state, fetchUsers]);

   const refresh = useCallback(() => {
      requestedKeyRef.current = null;
      setState({
         users: [],
         searchText: '',
         page: DEFAULT_PAGE,
         hasMore: true,
         loading: false,
         error: null,
      });
   }, []);

   return {
      data,
      searchText: state.searchText,
      loading: state.loading,
      hasMore: state.hasMore,
      error: state.error,
      handleSearch,
      loadMore,
      refresh,
   };
};
